// LearnCore Frontend - 用户中心页面

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import { ttsService, TTSService } from '../services/ttsService';
import Loading from '../components/Loading';
import type { UserSettings, TTSSettings } from '../types';
import { 
  UserCircleIcon,
  SpeakerWaveIcon,
  ArrowLeftIcon,
  PlayIcon,
  CheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

export default function UserCenterPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [settings, setSettings] = useState<UserSettings | null>(null);
  // 移除未使用的availableVoices状态
  const [testText, setTestText] = useState({
    chinese: '你好，这是中文测试语音',
    english: 'Hello, this is English test voice'
  });
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 先从TTS服务加载本地设置
      ttsService.loadSettings();
      const ttsSettings = ttsService.getSettings();
      
      // 尝试从服务器获取设置（如果失败则使用本地设置）
      try {
        const response = await apiClient.getUserSettings();
        setSettings(response.settings);
        ttsService.updateSettings(response.settings.tts);
      } catch (serverError) {
        // 服务器获取失败，使用本地设置
        setSettings({
          tts: ttsSettings
        });
      }
    } catch (err: any) {
      setError('加载设置失败');
      console.error('加载设置失败:', err);
      
      // 使用默认设置
      const defaultSettings: UserSettings = {
        tts: {
          enabled: true,
          rate: 1.0,
          pitch: 1.0,
          volume: 1.0
        }
      };
      setSettings(defaultSettings);
    } finally {
      setLoading(false);
    }
  };

  // 移除未使用的loadVoices函数

  const updateTTSSettings = (newTTSSettings: Partial<TTSSettings>) => {
    if (!settings) return;
    
    const updatedSettings = {
      ...settings,
      tts: {
        ...settings.tts,
        ...newTTSSettings
      }
    };
    
    setSettings(updatedSettings);
    ttsService.updateSettings(updatedSettings.tts);
  };

  const updateProfileSettings = (newProfile: Partial<NonNullable<UserSettings['profile']>>) => {
    if (!settings) return;

    const updatedSettings: UserSettings = {
      ...settings,
      profile: {
        ...(settings.profile || {}),
        ...newProfile
      }
    };
    setSettings(updatedSettings);
  };

  const saveSettings = async () => {
    if (!settings) return;
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      await apiClient.updateUserSettings(settings);
      setSuccess('设置保存成功');
      
      // 清除成功消息
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || '保存设置失败');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!settings) return;
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingAvatar(true);
      setError(null);
      const { image_url } = await apiClient.uploadImage(file);
      updateProfileSettings({ avatar_url: image_url });
      setSuccess('头像上传成功');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || '头像上传失败');
    } finally {
      setUploadingAvatar(false);
      e.currentTarget.value = '';
    }
  };

  const removeAvatar = () => {
    updateProfileSettings({ avatar_url: undefined });
  };

  const updateAdaptiveSetting = (key: 'flashcard' | 'definitionQuiz' | 'termQuiz', value: boolean) => {
    if (!settings) return;
    const updatedSettings: UserSettings = {
      ...settings,
      adaptive: {
        flashcard: settings.adaptive?.flashcard ?? false,
        definitionQuiz: settings.adaptive?.definitionQuiz ?? false,
        termQuiz: settings.adaptive?.termQuiz ?? false,
        [key]: value
      }
    };
    setSettings(updatedSettings);
  };

  const testVoice = async (text: string, language: 'chinese' | 'english') => {
    try {
      // 临时设置测试语音
      const testSettings = { ...settings?.tts };
      if (language === 'chinese' && testSettings) {
        // 使用选中的中文语音
      } else if (language === 'english' && testSettings) {
        // 使用选中的英文语音
      }
      
      await ttsService.speak(text);
    } catch (err) {
      setError('语音测试失败');
    }
  };

  const getChineseVoices = () => {
    return ttsService.getChineseVoices();
  };

  const getEnglishVoices = () => {
    return ttsService.getEnglishVoices();
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Loading size="lg" text="加载用户设置..." />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            加载设置失败
          </h1>
          <button
            onClick={loadSettings}
            className="btn-primary"
          >
            重试
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 页面头部 */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <Link
            to="/dashboard"
            className="flex items-center text-gray-600 hover:text-gray-800 transition-colors mr-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            返回仪表盘
          </Link>
        </div>
        
        <div className="flex items-center space-x-3">
          {settings.profile?.avatar_url ? (
            <img
              src={settings.profile.avatar_url}
              alt="avatar"
              className="h-10 w-10 rounded-full object-cover border"
            />
          ) : (
            <UserCircleIcon className="h-10 w-10 text-primary-600" />
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {settings.profile?.nickname || '用户中心'}
            </h1>
            <p className="text-gray-600">{user?.email}</p>
          </div>
        </div>
      </div>

      {/* 消息提示 */}
      {error && (
        <div className="mb-6 p-4 border border-danger-200 rounded-md bg-danger-50">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-danger-400" />
            <div className="ml-3">
              <p className="text-sm text-danger-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 border border-success-200 rounded-md bg-success-50">
          <div className="flex">
            <CheckIcon className="h-5 w-5 text-success-400" />
            <div className="ml-3">
              <p className="text-sm text-success-800">{success}</p>
            </div>
          </div>
        </div>
      )}

      {/* 个人资料设置 */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg mb-8">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center space-x-3 mb-6">
            <UserCircleIcon className="h-6 w-6 text-primary-600" />
            <h2 className="text-lg font-medium text-gray-900">个人资料</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* 头像 */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">头像</label>
              <div className="flex items-center space-x-4">
                {settings.profile?.avatar_url ? (
                  <img
                    src={settings.profile.avatar_url}
                    alt="avatar"
                    className="h-16 w-16 rounded-full object-cover border"
                  />
                ) : (
                  <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center border">
                    <UserCircleIcon className="h-8 w-8 text-gray-400" />
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <label className="btn-outline cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleAvatarFileChange}
                      disabled={uploadingAvatar}
                    />
                    {uploadingAvatar ? '上传中...' : '更换头像'}
                  </label>
                  {settings.profile?.avatar_url && (
                    <button type="button" className="btn-outline" onClick={removeAvatar}>
                      移除
                    </button>
                  )}
                </div>
              </div>
            </div>
            {/* 昵称 */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-900 mb-2">昵称</label>
              <input
                type="text"
                value={settings.profile?.nickname || ''}
                onChange={(e) => updateProfileSettings({ nickname: e.target.value || undefined })}
                placeholder="输入昵称"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">昵称将显示在你的公开学习集与排行榜中。</p>
            </div>
          </div>
        </div>
      </div>

      {/* 自适应学习设置 */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg mb-8">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center space-x-3 mb-6">
            <CheckIcon className="h-6 w-6 text-primary-600" />
            <h2 className="text-lg font-medium text-gray-900">
              自适应学习（微型SRS）
            </h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-900">抽认卡（闪卡）启用</label>
                <p className="text-sm text-gray-500">基于错题优先的队列重复，提升掌握速度</p>
              </div>
              <button
                type="button"
                className={`${(settings.adaptive?.flashcard ?? false) ? 'bg-primary-600 focus:ring-primary-500' : 'bg-gray-200 focus:ring-gray-500'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2`}
                onClick={() => updateAdaptiveSetting('flashcard', !(settings.adaptive?.flashcard ?? false))}
              >
                <span
                  className={`${(settings.adaptive?.flashcard ?? false) ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white dark:bg-gray-300 shadow ring-0 transition duration-200 ease-in-out`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-900">定义测验启用</label>
                <p className="text-sm text-gray-500">按术语出题，定义为选项；错题优先</p>
              </div>
              <button
                type="button"
                className={`${(settings.adaptive?.definitionQuiz ?? false) ? 'bg-primary-600 focus:ring-primary-500' : 'bg-gray-200 focus:ring-gray-500'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2`}
                onClick={() => updateAdaptiveSetting('definitionQuiz', !(settings.adaptive?.definitionQuiz ?? false))}
              >
                <span
                  className={`${(settings.adaptive?.definitionQuiz ?? false) ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white dark:bg-gray-300 shadow ring-0 transition duration-200 ease-in-out`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-900">术语测验启用</label>
                <p className="text-sm text-gray-500">按定义出题，术语为选项；错题优先</p>
              </div>
              <button
                type="button"
                className={`${(settings.adaptive?.termQuiz ?? false) ? 'bg-primary-600 focus:ring-primary-500' : 'bg-gray-200 focus:ring-gray-500'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2`}
                onClick={() => updateAdaptiveSetting('termQuiz', !(settings.adaptive?.termQuiz ?? false))}
              >
                <span
                  className={`${(settings.adaptive?.termQuiz ?? false) ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white dark:bg-gray-300 shadow ring-0 transition duration-200 ease-in-out`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* TTS设置 */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center space-x-3 mb-6">
            <SpeakerWaveIcon className="h-6 w-6 text-primary-600" />
            <h2 className="text-lg font-medium text-gray-900">
              语音朗读设置
            </h2>
          </div>

          {!TTSService.isSupported() && (
            <div className="mb-6 p-4 border border-warning-200 rounded-md bg-warning-50">
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-warning-400" />
                <div className="ml-3">
                  <p className="text-sm text-warning-800">
                    您的浏览器不支持语音朗读功能
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {/* 启用TTS */}
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-900">
                  启用语音朗读
                </label>
                <p className="text-sm text-gray-500">
                  在学习闪卡时可以朗读术语和定义
                </p>
              </div>
              <button
                type="button"
                className={`${
                  settings.tts.enabled 
                    ? 'bg-primary-600 focus:ring-primary-500' 
                    : 'bg-gray-200 focus:ring-gray-500'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2`}
                onClick={() => updateTTSSettings({ enabled: !settings.tts.enabled })}
              >
                <span
                  className={`${
                    settings.tts.enabled ? 'translate-x-5' : 'translate-x-0'
                  } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white dark:bg-gray-300 shadow ring-0 transition duration-200 ease-in-out`}
                />
              </button>
            </div>

            {settings.tts.enabled && (
              <>
                {/* 中文语音选择 */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    中文语音
                  </label>
                  <div className="flex space-x-2">
                    <select
                      value={settings.tts.chineseVoice || ''}
                      onChange={(e) => updateTTSSettings({ chineseVoice: e.target.value || undefined })}
                      className="flex-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    >
                      <option value="">选择中文语音...</option>
                      {getChineseVoices().map((voice) => (
                        <option key={voice.voiceURI} value={voice.voiceURI}>
                          {voice.name} ({voice.lang})
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => testVoice(testText.chinese, 'chinese')}
                      className="btn-outline flex items-center"
                      disabled={!settings.tts.chineseVoice}
                    >
                      <PlayIcon className="h-4 w-4 mr-1" />
                      测试
                    </button>
                  </div>
                  <input
                    type="text"
                    value={testText.chinese}
                    onChange={(e) => setTestText(prev => ({ ...prev, chinese: e.target.value }))}
                    placeholder="输入中文测试文本..."
                    className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>

                {/* 英文语音选择 */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    英文语音
                  </label>
                  <div className="flex space-x-2">
                    <select
                      value={settings.tts.englishVoice || ''}
                      onChange={(e) => updateTTSSettings({ englishVoice: e.target.value || undefined })}
                      className="flex-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    >
                      <option value="">选择英文语音...</option>
                      {getEnglishVoices().map((voice) => (
                        <option key={voice.voiceURI} value={voice.voiceURI}>
                          {voice.name} ({voice.lang})
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => testVoice(testText.english, 'english')}
                      className="btn-outline flex items-center"
                      disabled={!settings.tts.englishVoice}
                    >
                      <PlayIcon className="h-4 w-4 mr-1" />
                      测试
                    </button>
                  </div>
                  <input
                    type="text"
                    value={testText.english}
                    onChange={(e) => setTestText(prev => ({ ...prev, english: e.target.value }))}
                    placeholder="Enter English test text..."
                    className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>

                {/* 语音参数 */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* 语速 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      语速: {settings.tts.rate.toFixed(1)}
                    </label>
                    <input
                      type="range"
                      min="0.5"
                      max="2"
                      step="0.1"
                      value={settings.tts.rate}
                      onChange={(e) => updateTTSSettings({ rate: parseFloat(e.target.value) })}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>慢</span>
                      <span>正常</span>
                      <span>快</span>
                    </div>
                  </div>

                  {/* 音调 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      音调: {settings.tts.pitch.toFixed(1)}
                    </label>
                    <input
                      type="range"
                      min="0.5"
                      max="2"
                      step="0.1"
                      value={settings.tts.pitch}
                      onChange={(e) => updateTTSSettings({ pitch: parseFloat(e.target.value) })}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>低</span>
                      <span>正常</span>
                      <span>高</span>
                    </div>
                  </div>

                  {/* 音量 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      音量: {Math.round(settings.tts.volume * 100)}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={settings.tts.volume}
                      onChange={(e) => updateTTSSettings({ volume: parseFloat(e.target.value) })}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>静音</span>
                      <span>50%</span>
                      <span>100%</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* 保存按钮 */}
        <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            onClick={saveSettings}
            disabled={saving}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
          >
            {saving ? '保存中...' : '保存设置'}
          </button>
        </div>
      </div>
    </div>
  );
}
